import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// System Health — visualizes the /strategies/api/strategies/mansa-monitor/health/
// diagnostic. Buffer warmth, VIX freshness, model load state, today's prob
// distribution + warnings. Auto-refreshes every 30s so you can leave it open.

const REFRESH_MS = 30000;

function StatusDot({ ok, warn }) {
  const c = ok ? T.bull : (warn ? T.gold : T.bear);
  return (
    <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
                   background: c, marginRight: 6, verticalAlign: 'middle' }} />
  );
}

function Pill({ label, ok, warn, mono = false }) {
  const c = ok ? T.bull : warn ? T.gold : T.bear;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                   border: `1px solid ${c}`, color: c, fontWeight: 700,
                   fontSize: 10, letterSpacing: '0.06em',
                   fontFamily: mono ? 'Roboto Mono, monospace' : 'inherit' }}>
      {label}
    </span>
  );
}

function KV({ k, v, accent }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                  padding: '4px 0', borderTop: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.05em',
                     textTransform: 'uppercase' }}>{k}</span>
      <span style={{ fontSize: 12, fontFamily: 'Roboto Mono, monospace',
                     color: accent || T.text, fontWeight: 600 }}>{v}</span>
    </div>
  );
}

function fmtAge(min) {
  if (min == null) return '—';
  if (min < 1)   return `${Math.round(min * 60)}s`;
  if (min < 60)  return `${min.toFixed(1)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}h ${m}m`;
}

function BufferCard({ asset, buf }) {
  if (!buf) return null;
  const ageMin = buf.age_min;
  const stale  = !buf.fresh;
  const ageWarn = stale;
  // Expected last-hour count for a 3m cadence in-session is ~20. VIX in
  // overnight hours legitimately has none; only flag when last bar is stale.
  const expectHr = buf.expected_bars_last_hour || 20;
  const lastHrLow = stale && buf.actual_bars_last_hour < (expectHr * 0.5);

  return (
    <div style={{ padding: '14px 16px', borderRadius: 8, background: T.bg,
                  border: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline',
                    marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text }}>
          {asset} buffer
        </h3>
        <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
          candles_3m · asset={asset}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Pill label={buf.fresh ? 'FRESH' : 'STALE'} ok={buf.fresh} warn={false} />
          <Pill label={buf.fully_warm ? '600+' : (buf.warm ? '200+' : 'COLD')}
                ok={buf.fully_warm} warn={buf.warm && !buf.fully_warm} />
          <Pill label={buf.contiguous_last_200 ? 'CONTIGUOUS' : 'GAPS'}
                ok={buf.contiguous_last_200} warn={false} />
        </span>
      </div>
      <KV k="Total rows"  v={buf.rows_total != null ? buf.rows_total.toLocaleString() : '—'} />
      <KV k="Last 24h"    v={buf.rows_last_24h != null ? buf.rows_last_24h.toLocaleString() : '—'} />
      <KV k="Last bar (ET)" v={buf.last_bar_et || '—'} />
      <KV k="Bar age"     v={fmtAge(ageMin)} accent={ageWarn ? T.bear : T.text} />
      <KV k="Bars in last hour"
          v={`${buf.actual_bars_last_hour} / ${expectHr}`}
          accent={lastHrLow ? T.bear : T.text} />
      <KV k="Intra-session gaps (last 200)"
          v={buf.intra_session_gaps_last_200 != null ? buf.intra_session_gaps_last_200 : '—'}
          accent={buf.intra_session_gaps_last_200 > 0 ? T.gold : T.text} />
    </div>
  );
}

function ModelCard({ id, m }) {
  if (!m) return null;
  return (
    <div style={{ padding: '14px 16px', borderRadius: 8, background: T.bg,
                  border: `1px solid ${T.border}`, flex: 1, minWidth: 'min(100%, 260px)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10, gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text }}>
          {id} decider
        </h3>
        <Pill label={m.decider_loaded ? 'LOADED' : 'NOT LOADED'} ok={!!m.decider_loaded} />
        {id === 'GBEMI' && m.volgate_enabled != null && (
          <Pill label={m.volgate_enabled ? 'VOLGATE ON' : 'VOLGATE OFF'}
                ok={m.volgate_enabled} warn={!m.volgate_enabled} />
        )}
      </div>
      {m.load_error && (
        <div style={{ padding: 8, fontSize: 11, color: T.bear,
                      background: 'rgba(242,54,69,0.08)',
                      borderRadius: 4, marginBottom: 8 }}>
          {m.load_error}
        </div>
      )}
      {id === 'GBEMI' && m.books && m.books.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ color: T.textMuted, textAlign: 'left',
                          fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <th style={{ padding: '4px 6px' }}>Book</th>
              <th style={{ padding: '4px 6px' }}>Side</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Thresh</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Feats</th>
            </tr>
          </thead>
          <tbody>
            {m.books.map(b => (
              <tr key={b.book} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ padding: '4px 6px', fontFamily: 'Roboto Mono, monospace',
                             color: T.text }}>{b.book}</td>
                <td style={{ padding: '4px 6px', color: b.side === 'long' ? T.bull : T.bear,
                             fontWeight: 700 }}>{(b.side || '').toUpperCase()}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right',
                             fontFamily: 'Roboto Mono, monospace' }}>{b.threshold}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right',
                             fontFamily: 'Roboto Mono, monospace', color: T.textMuted }}>{b.features}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {id === 'PADA' && (
        <>
          <KV k="Side" v={m.side || '—'} />
          <KV k="Threshold (live)"
              v={m.threshold != null ? m.threshold : '—'} />
          <KV k="Threshold (config)"
              v={m.threshold_config != null ? m.threshold_config : '—'} />
          <KV k="Features" v={m.features != null ? m.features : '—'} />
        </>
      )}
    </div>
  );
}

function ProbHistogram({ buckets, threshold }) {
  if (!buckets || !buckets.length) return null;
  const max = Math.max(...buckets.map(b => b.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80,
                  padding: '6px 0', borderTop: `1px solid ${T.border}` }}>
      {buckets.map((b, i) => {
        const h = (b.count / max) * 70;
        // Bucket "0.5-0.6" — bucket center 0.55. Highlight bars that straddle
        // a typical threshold (~0.5) so the operator immediately sees where
        // signals cluster relative to it.
        const center = (i + 0.5) / 10;
        const aboveThr = threshold != null && center >= threshold;
        const c = aboveThr ? T.bull : T.bear;
        return (
          <div key={b.bucket}
               title={`${b.bucket} — ${b.count} signals`}
               style={{ flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', height: Math.max(2, h),
                          background: c, opacity: b.count > 0 ? 0.85 : 0.25,
                          borderRadius: 2 }} />
            <div style={{ fontSize: 8, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
              {b.bucket.split('-')[0]}
            </div>
            <div style={{ fontSize: 9, color: T.text, fontFamily: 'Roboto Mono, monospace',
                          fontWeight: 700 }}>
              {b.count}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TodayCard({ id, t, refThreshold }) {
  if (!t) return null;
  const total = t.signals_total || 0;
  const vixTotal = (t.vix_null_count || 0) + (t.vix_present_count || 0);
  const vixNullPct = vixTotal > 0 ? Math.round((t.vix_null_count / vixTotal) * 100) : 0;
  const clusteredPct = t.prob_stats?.clustered_30_55_pct
    ? Math.round(t.prob_stats.clustered_30_55_pct * 100) : 0;
  return (
    <div style={{ padding: '14px 16px', borderRadius: 8, background: T.bg,
                  border: `1px solid ${T.border}`, flex: 1, minWidth: 'min(100%, 320px)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10, gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text }}>
          {id} · Today
        </h3>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {total} signal{total === 1 ? '' : 's'} · {t.trades} TRADE · {t.blocks} BLOCK
        </span>
      </div>
      <KV k="Prob non-null" v={`${t.prob_stats?.non_null_count || 0} / ${total}`} />
      {t.prob_stats?.non_null_count > 0 && (
        <>
          <KV k="Prob min / median / max"
              v={`${t.prob_stats.min} / ${t.prob_stats.median} / ${t.prob_stats.max}`} />
          <KV k="Clustered in [0.30, 0.55]"
              v={`${clusteredPct}% (${Math.round((t.prob_stats.clustered_30_55_pct || 0) * (t.prob_stats.non_null_count || 0))} of ${t.prob_stats.non_null_count})`}
              accent={clusteredPct >= 80 && t.prob_stats.non_null_count >= 10 ? T.gold : T.text} />
        </>
      )}
      <KV k="VIX null on signals"
          v={`${t.vix_null_count} / ${vixTotal} (${vixNullPct}%)`}
          accent={vixNullPct > 25 && vixTotal >= 3 ? T.gold : T.text} />
      {t.prob_stats?.non_null_count > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: '0.08em',
                        textTransform: 'uppercase', marginBottom: 2 }}>
            Prob distribution (bucket center vs threshold {refThreshold ?? '?'})
          </div>
          <ProbHistogram buckets={t.prob_distribution} threshold={refThreshold} />
        </div>
      )}
      {t.reason_codes && t.reason_codes.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: T.textDim }}>
          <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: '0.08em',
                        textTransform: 'uppercase', marginBottom: 4 }}>
            Reason codes
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {t.reason_codes.map(rc => (
              <span key={rc.code}
                    style={{ padding: '2px 6px', borderRadius: 3,
                             background: 'rgba(242,54,69,0.08)',
                             border: `1px solid rgba(242,54,69,0.25)`,
                             color: T.text, fontFamily: 'Roboto Mono, monospace',
                             fontSize: 10 }}>
                {rc.code} <strong style={{ color: T.bear }}>({rc.count})</strong>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VerdictBanner({ verdict }) {
  if (!verdict) return null;
  const ok = verdict.safe_to_trust_model_skips;
  const warnCount = (verdict.warnings || []).length;
  const c = ok && warnCount === 0 ? T.bull : (warnCount === 0 ? T.gold : T.bear);
  return (
    <div style={{ padding: '14px 18px', borderRadius: 8, marginBottom: 14,
                  background: `${c}14`,                  // 8% opacity-ish
                  border: `1px solid ${c}`,
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <StatusDot ok={ok && warnCount === 0} warn={warnCount > 0 && warnCount < 3} />
      <span style={{ fontSize: 14, fontWeight: 700, color: c,
                     letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {ok && warnCount === 0 ? 'All systems healthy'
                                : `${warnCount} warning${warnCount === 1 ? '' : 's'} — review below`}
      </span>
      <span style={{ fontSize: 11, color: T.textDim, fontStyle: 'italic' }}>
        {verdict.summary}
      </span>
    </div>
  );
}

function WarningsList({ warnings }) {
  if (!warnings || !warnings.length) return null;
  return (
    <div style={{ padding: '14px 18px', borderRadius: 8, marginBottom: 14,
                  background: T.bg, border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: T.textMuted, marginBottom: 8 }}>
        Warnings
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: T.text, lineHeight: 1.6 }}>
        {warnings.map((w, i) => (
          <li key={i} style={{ color: w.includes('cannot compute') ? T.bear : T.gold }}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

export default function SystemHealth() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const timerRef = useRef(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/strategies/api/strategies/mansa-monitor/health/`,
        { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchHealth();
    timerRef.current = setInterval(fetchHealth, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchHealth]);

  const buf = data?.buffer || {};
  const models = data?.models || {};
  const today  = data?.today || {};
  const pause  = data?.pause_state || {};
  const verdict = data?.verdict;

  // Pull a reference threshold for each strategy's histogram. GBEMI has
  // multiple book-specific thresholds; we use the LONGS one as the visual
  // anchor (most signals are longs). PADÀ has a single threshold.
  const gbemiThr = (models.GBEMI?.books || []).find(b => b.book === 'GBEMI_LONGS_v1')?.threshold;
  const padaThr  = models.PADA?.threshold ?? models.PADA?.threshold_config;

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1500, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                    paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
          System Health
        </h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          GBEMI + PADÀ pipeline diagnostic · buffer warmth · VIX freshness · model state
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center',
                      flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 320px' }}>
          {lastFetched && (
            <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
              updated {lastFetched.toLocaleTimeString('en-GB', { hour12: false, timeZone: 'America/New_York' })} ET
            </span>
          )}
          <span style={{ fontSize: 10, color: T.textDim }}>
            auto-refresh every {REFRESH_MS / 1000}s
          </span>
          <button onClick={fetchHealth} disabled={loading}
                  style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
                           letterSpacing: '0.07em', textTransform: 'uppercase',
                           borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
                           border: 'none', background: 'rgba(16,185,129,0.18)',
                           color: '#10b981' }}>
            {loading ? 'Checking…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, borderRadius: 6,
                      background: 'rgba(242,54,69,0.08)', border: `1px solid rgba(242,54,69,0.3)`,
                      color: T.bear, fontSize: 12, marginBottom: 16 }}>
          Failed to load health: {error}
        </div>
      )}

      <VerdictBanner verdict={verdict} />
      <WarningsList warnings={verdict?.warnings} />

      {/* Pause state — only render if anything is paused */}
      {(pause.GBEMI_PAUSED || pause.PADA_PAUSED) && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14,
                      background: 'rgba(245,158,11,0.10)',
                      border: `1px solid ${T.gold}`, color: T.gold,
                      fontSize: 12, fontWeight: 700 }}>
          ⚠ Kill switch active:
          {pause.GBEMI_PAUSED ? ' GBEMI_PAUSED' : ''}
          {pause.PADA_PAUSED  ? ' PADA_PAUSED'  : ''}
        </div>
      )}

      {/* Buffer health */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 'min(100%, 320px)' }}>
          <BufferCard asset="NQ"  buf={buf.NQ}  />
        </div>
        <div style={{ flex: 1, minWidth: 'min(100%, 320px)' }}>
          <BufferCard asset="VIX" buf={buf.VIX} />
        </div>
      </div>

      {/* Model state */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
        <ModelCard id="GBEMI" m={models.GBEMI} />
        <ModelCard id="PADA"  m={models.PADA}  />
      </div>

      {/* Today's signal stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
        <TodayCard id="GBEMI" t={today.gbemi} refThreshold={gbemiThr} />
        <TodayCard id="PADA"  t={today.pada}  refThreshold={padaThr} />
      </div>

      {/* Server timestamps + window — useful when debugging staleness */}
      {data && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14,
                      background: T.bg, border: `1px solid ${T.border}`,
                      fontSize: 11, color: T.textDim, fontFamily: 'Roboto Mono, monospace' }}>
          server clock: {data.timestamp_et || data.timestamp_utc} ·
          window: {data.since_utc} → {data.until_utc} (ET date {data.et_date})
        </div>
      )}
    </div>
    </div>
  );
}
